import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { ProfilePhotoUpload } from "@/components/host/settings/ProfilePhotoUpload";
import { PlatformConnections } from "@/components/host/settings/PlatformConnections";
import { NotificationSettings } from "@/components/host/settings/NotificationSettings";
import { PaymentMethodsManager } from "@/components/host/settings/PaymentMethodsManager";
import { SecurityTab } from "@/components/guest/preferences/SecurityTab";
import { TravelPreferencesTab } from "@/components/guest/preferences/TravelPreferencesTab";
import { SubscriptionManagement } from "@/components/host/settings/SubscriptionManagement";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { NotificationPreferences, ProfileData, BillingInfo } from "@/types/settings";

const HostSettingsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['host-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      // Parse notification preferences with type safety
      const rawPreferences = data?.notification_preferences as Record<string, boolean> | null;
      const notificationPreferences: NotificationPreferences = {
        booking_updates: rawPreferences?.booking_updates ?? true,
        payment_confirmation: rawPreferences?.payment_confirmation ?? true,
        marketing_emails: rawPreferences?.marketing_emails ?? false,
      };

      // Parse billing info with type safety
      const rawBillingInfo = data?.billing_info as Record<string, string> | null;
      const billingInfo: BillingInfo | null = rawBillingInfo ? {
        fullName: rawBillingInfo.fullName || '',
        address: rawBillingInfo.address || '',
        city: rawBillingInfo.city || '',
        country: rawBillingInfo.country || '',
        postalCode: rawBillingInfo.postalCode || '',
      } : null;

      // Parse the profile data with proper type casting
      const profileData: ProfileData = {
        id: data?.id ?? '',
        full_name: data?.full_name,
        phone_number: data?.phone_number,
        notification_preferences: notificationPreferences,
        preferred_currency: data?.preferred_currency,
        preferred_language: data?.preferred_language,
        role: data?.role ?? 'guest',
        verification_status: data?.verification_status,
        is_verified: data?.is_verified,
        verification_submitted_at: data?.verification_submitted_at,
        onboarding_completed: data?.onboarding_completed,
        onboarding_step: data?.onboarding_step,
        host_onboarding_completed: data?.host_onboarding_completed,
        host_onboarding_step: data?.host_onboarding_step,
        email_verified_reminder_sent: data?.email_verified_reminder_sent,
        onboarding_data: data?.onboarding_data as ProfileData['onboarding_data'],
        is_blocked: data?.is_blocked ?? false,
        verification_steps: data?.verification_steps as ProfileData['verification_steps'],
        payment_details: data?.payment_details as ProfileData['payment_details'],
        payment_details_verified: data?.payment_details_verified ?? false,
        payout_preferences: data?.payout_preferences as ProfileData['payout_preferences'],
        split_payment_preferences: data?.split_payment_preferences as ProfileData['split_payment_preferences'],
        host_onboarding_data: data?.host_onboarding_data as ProfileData['host_onboarding_data'],
        billing_info: billingInfo,
        profile_photo_url: data?.profile_photo_url,
        created_at: data?.created_at ?? '',
        updated_at: data?.updated_at ?? ''
      };

      return profileData;
    }
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (updates: Partial<ProfileData>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Convert the updates to a plain object that Supabase can handle
      const supabaseUpdates = {
        ...updates,
        notification_preferences: updates.notification_preferences ? 
          JSON.parse(JSON.stringify(updates.notification_preferences)) : undefined,
        billing_info: updates.billing_info ? 
          JSON.parse(JSON.stringify(updates.billing_info)) : undefined
      };

      const { error } = await supabase
        .from('profiles')
        .update(supabaseUpdates)
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host-profile'] });
      toast({
        title: "Success",
        description: "Settings updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    }
  });

  const handlePhotoUpload = async (url: string) => {
    try {
      await updateProfileMutation.mutateAsync({ profile_photo_url: url });
    } catch (error) {
      console.error("Error updating profile photo:", error);
    }
  };

  const handleNotificationSettingsSubmit = async (preferences: NotificationPreferences) => {
    setIsLoading(true);
    try {
      await updateProfileMutation.mutateAsync({ 
        notification_preferences: preferences 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container py-6 space-y-6">
      <h1 className="text-3xl font-bold">Settings</h1>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="platforms">Platforms</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="preferences">Travel Preferences</TabsTrigger>
          <TabsTrigger value="subscription">AI Assistant</TabsTrigger>
        </TabsList>

        <div className="mt-6 space-y-6">
          <TabsContent value="profile">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Profile Photo</h2>
              <ProfilePhotoUpload 
                currentPhotoUrl={profile?.profile_photo_url} 
                onUpload={handlePhotoUpload} 
              />
            </Card>
          </TabsContent>

          <TabsContent value="platforms">
            <PlatformConnections />
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationSettings 
              preferences={profile?.notification_preferences || {
                booking_updates: true,
                payment_confirmation: true,
                marketing_emails: false,
              }}
              onSubmit={handleNotificationSettingsSubmit}
              isLoading={isLoading}
            />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentMethodsManager />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>

          <TabsContent value="preferences">
            <TravelPreferencesTab />
          </TabsContent>
          
          <TabsContent value="subscription">
            <SubscriptionManagement />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default HostSettingsPage;
